import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import { Store, Plus } from 'lucide-react';
import { STOCKIST_CONSIGNMENT_PERMS } from '@mlm/shared';
import { listStockistSummary } from '../../lib/api';
import { useHasPerm } from '../../lib/auth-store';

const peso = (v: string | number) =>
  `₱${Number(v).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Stockist-consignment landing page — the twin of the customer-consignment one,
 * but keyed on BRANCHES: a stockist owes as a branch, not as a person.
 *
 * "Receivable" here is raised by the stockist's OWN sale on our POS, never
 * typed by anyone, which is the difference this feature exists for.
 */
export function StockistBranchesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const canConsignOut = useHasPerm(STOCKIST_CONSIGNMENT_PERMS.consignOut);
  const query = useQuery({
    queryKey: ['stockist-summary', q],
    queryFn: () => listStockistSummary(q || undefined),
  });
  const rows = query.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Stockist Consignment</h1>
          <p className="text-sm text-muted-foreground">
            Stock in each stockist&apos;s hands, and what they owe once it sells.
          </p>
        </div>
        {canConsignOut && (
          <Button onClick={() => navigate('/stockist-consignment/new')}>
            <Plus className="mr-1 h-4 w-4" /> Consign Out
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">Stockists</CardTitle>
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                window.setTimeout(() => setQ(e.target.value), 300);
              }}
              placeholder="Search name or code…"
              className="w-64"
            />
          </div>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center">
              <Store className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm font-medium">No stockists holding stock yet</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                Consign stock out to a stockist branch. When they ring up a sale on our POS, the
                receivable is raised automatically at the price you consigned it at.
              </p>
              {canConsignOut && (
                <Button className="mt-4" onClick={() => navigate('/stockist-consignment/new')}>
                  Consign Out
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-3">Stockist</th>
                    <th className="py-2 pr-3">Code</th>
                    <th className="py-2 pr-3 text-right">Units out</th>
                    <th className="py-2 pr-3 text-right">Open billings</th>
                    <th className="py-2 pr-3 text-right">Receivable</th>
                    <th className="py-2">Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.stockist_branch_id}
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/50"
                      onClick={() => navigate(`/stockist-consignment/${row.stockist_branch_id}`)}
                    >
                      <td className="py-2.5 pr-3 font-medium">{row.name}</td>
                      <td className="py-2.5 pr-3 text-muted-foreground">{row.code}</td>
                      <td className="py-2.5 pr-3 text-right">{row.units}</td>
                      <td className="py-2.5 pr-3 text-right">{row.open_billings}</td>
                      <td className="py-2.5 pr-3 text-right font-medium">{peso(row.receivable)}</td>
                      <td className="py-2.5 text-muted-foreground">
                        {row.last_activity ? new Date(row.last_activity).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
